import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Dialog,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import type { IHost, IPartner } from '../../redux/discovery/types';
import { searchHost, searchPartner } from '../../redux/contest/actions';
import {
    hostSearchSelector,
    partnerSearchSelector,
    searchingHostSelector,
    searchingPartnerSelector,
} from '../AvatarGroup/selector';
import {
    formatRecentInteractionLabel,
    getRecentHosts,
    getRecentPartners,
    pushRecentHost,
    pushRecentPartner,
    recentEntryToHost,
    type RecentHostPartnerEntry,
} from './hostPartnerRecentStorage';

const midnight = {
    primary: '#ffc965',
    primaryFixed: '#392700',
    secondary: '#00fdc1',
    surface: '#0e0e0e',
    surfaceLow: '#131313',
    surfaceContainer: '#1a1919',
    surfaceContainerHigh: '#201f1f',
    surfaceHighest: '#262626',
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outlineVariant: '#484847',
};

type Entity = IHost | IPartner;

export interface ContestHostPartnerPickerModalProps {
    open: boolean;
    onClose: () => void;
    searchType: 'host' | 'partner';
    value?: IHost | IPartner[];
    onConfirm: (value: IHost | IPartner[] | undefined) => void;
    onCreateNew: () => void;
    placeholder?: string;
}

function entitySubtitle(entity: Entity | RecentHostPartnerEntry): string {
    const desc = entity.description?.trim();
    if (desc) return desc.length > 48 ? `${desc.slice(0, 48)}…` : desc;
    return 'Creative partner';
}

function entityAvatarSrc(entity: Entity | RecentHostPartnerEntry): string | undefined {
    return entity.profile_picture?.trim() || undefined;
}

function PartnerListRow({ entity, selected, onToggle }: { entity: Entity; selected: boolean; onToggle: () => void }) {
    const src = entityAvatarSrc(entity);
    return (
        <Box
            component="button"
            type="button"
            onClick={onToggle}
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 2,
                border: 'none',
                borderRadius: '12px',
                bgcolor: midnight.surfaceLow,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.2s, transform 0.15s',
                '&:hover': { bgcolor: midnight.surfaceContainer },
                '&:active': { transform: 'scale(0.98)' },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Avatar
                    src={src}
                    alt={entity.name}
                    sx={{
                        width: 56,
                        height: 56,
                        border: `2px solid ${selected ? 'rgba(254,183,0,0.45)' : 'rgba(254,183,0,0.12)'}`,
                        bgcolor: midnight.surfaceHighest,
                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                        fontWeight: 800,
                    }}>
                    {entity.name?.charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: 18,
                            color: midnight.onSurface,
                            lineHeight: 1.2,
                        }}>
                        {entity.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: midnight.onSurfaceVariant,
                            fontWeight: 500,
                            mt: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {entitySubtitle(entity)}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: `1px solid ${selected ? 'rgba(255,201,101,0.45)' : midnight.outlineVariant}`,
                    bgcolor: selected ? 'rgba(255,201,101,0.1)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                {selected ? <CheckIcon sx={{ fontSize: 16, color: midnight.primary }} /> : null}
            </Box>
        </Box>
    );
}

function RecentGridCard({
    entry,
    active,
    onSelect,
}: {
    entry: RecentHostPartnerEntry;
    active: boolean;
    onSelect: () => void;
}) {
    const src = entityAvatarSrc(entry);
    return (
        <Box
            component="button"
            type="button"
            onClick={onSelect}
            sx={{
                border: active ? `1px solid rgba(255,201,101,0.35)` : 'none',
                borderRadius: '16px',
                bgcolor: midnight.surfaceContainer,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: midnight.surfaceContainerHigh },
            }}>
            <Avatar
                src={src}
                alt={entry.name}
                sx={{
                    width: 64,
                    height: 64,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                    bgcolor: midnight.surfaceHighest,
                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                    fontWeight: 800,
                }}>
                {entry.name?.charAt(0)}
            </Avatar>
            <Box sx={{ textAlign: 'center' }}>
                <Typography
                    sx={{
                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                        fontWeight: 800,
                        fontSize: 14,
                        color: midnight.onSurface,
                    }}>
                    {entry.name}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: active ? midnight.secondary : midnight.onSurfaceVariant,
                        mt: 0.5,
                    }}>
                    {active ? 'Selected' : formatRecentInteractionLabel(entry.selectedAt)}
                </Typography>
            </Box>
        </Box>
    );
}

export function ContestHostPartnerPickerModal({
    open,
    onClose,
    searchType,
    value,
    onConfirm,
    onCreateNew,
    placeholder,
}: ContestHostPartnerPickerModalProps) {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const hostResults = useSelector(hostSearchSelector);
    const partnerResults = useSelector(partnerSearchSelector);
    const searchingHost = useSelector(searchingHostSelector);
    const searchingPartner = useSelector(searchingPartnerSelector);

    const [searchTerm, setSearchTerm] = useState('');
    const [pendingHost, setPendingHost] = useState<IHost | undefined>();
    const [pendingPartners, setPendingPartners] = useState<IPartner[]>([]);
    const [recentEntries, setRecentEntries] = useState<RecentHostPartnerEntry[]>([]);

    const isHost = searchType === 'host';
    const title = isHost ? 'Host Selection' : 'Partner Selection';
    const searchResults = (isHost ? hostResults : partnerResults) ?? [];
    const searching = isHost ? searchingHost : searchingPartner;

    const refreshRecent = useCallback(() => {
        setRecentEntries(isHost ? getRecentHosts() : getRecentPartners());
    }, [isHost]);

    useEffect(() => {
        if (!open) return;
        refreshRecent();
        setSearchTerm('');
        if (isHost) {
            setPendingHost(value && !Array.isArray(value) ? (value as IHost) : undefined);
            setPendingPartners([]);
        } else {
            setPendingPartners(Array.isArray(value) ? [...(value as IPartner[])] : []);
            setPendingHost(undefined);
        }
    }, [open, isHost, value, refreshRecent]);

    const debouncedSearch = useRef(
        _.debounce((term: string, type: 'host' | 'partner') => {
            if (term.trim().length < 2) return;
            if (type === 'host') {
                dispatch(searchHost({ searchTerm: term.trim() }));
            } else {
                dispatch(searchPartner({ searchTerm: term.trim() }));
            }
        }, 400),
    ).current;

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearchChange = (term: string) => {
        setSearchTerm(term);
        debouncedSearch(term, searchType);
    };

    const isSelected = useCallback(
        (entity: Entity) => {
            if (isHost) return pendingHost?._id === entity._id;
            return pendingPartners.some(p => p._id === entity._id);
        },
        [isHost, pendingHost?._id, pendingPartners],
    );

    const toggleEntity = useCallback(
        (entity: Entity) => {
            if (isHost) {
                setPendingHost(prev => (prev?._id === entity._id ? undefined : (entity as IHost)));
                return;
            }
            setPendingPartners(prev => {
                const exists = prev.some(p => p._id === entity._id);
                if (exists) return prev.filter(p => p._id !== entity._id);
                return [...prev, entity as IPartner];
            });
        },
        [isHost],
    );

    const handleConfirm = () => {
        if (isHost) {
            if (pendingHost) pushRecentHost(pendingHost);
            onConfirm(pendingHost);
        } else {
            pendingPartners.forEach(pushRecentPartner);
            onConfirm(pendingPartners);
        }
        refreshRecent();
        onClose();
    };

    const showSearchResults = searchTerm.trim().length >= 2;
    const filteredRecent = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return recentEntries;
        return recentEntries.filter(
            entry => entry.name.toLowerCase().includes(q) || (entry.description ?? '').toLowerCase().includes(q),
        );
    }, [recentEntries, searchTerm]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: midnight.surface,
                    backgroundImage: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isMobile ? 0 : 3,
                    border: isMobile ? 'none' : `1px solid ${midnight.outlineVariant}`,
                    height: isMobile ? '100%' : 'min(92vh, 880px)',
                    minHeight: isMobile ? '100%' : 'min(92vh, 880px)',
                    maxHeight: isMobile ? '100%' : 'min(92vh, 880px)',
                    m: isMobile ? 0 : 2,
                    overflow: 'hidden',
                },
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        pt: isMobile ? 4 : 3,
                        pb: 2,
                        flexShrink: 0,
                    }}>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: midnight.onSurfaceVariant }}>
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            color: midnight.primary,
                        }}>
                        {title}
                    </Typography>
                    <Box sx={{ width: 40 }} />
                </Box>

                <Box sx={{ px: 3, mb: 3 }}>
                    <TextField
                        fullWidth
                        autoFocus
                        placeholder={placeholder ?? `Search and select a ${searchType}…`}
                        value={searchTerm}
                        onChange={e => handleSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: midnight.onSurfaceVariant }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: 64,
                                borderRadius: 999,
                                bgcolor: 'rgba(38,38,38,0.4)',
                                backdropFilter: 'blur(12px)',
                                color: midnight.onSurface,
                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: 18,
                                '& fieldset': {
                                    borderColor: 'rgba(255,201,101,0.2)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255,201,101,0.35)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: midnight.primary,
                                    boxShadow: '0 0 15px rgba(255,201,101,0.1)',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(173,170,170,0.5)',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 2, minHeight: 0 }}>
                    {showSearchResults ? (
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                    fontSize: 10,
                                    fontWeight: 900,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: midnight.onSurfaceVariant,
                                    mb: 2,
                                }}>
                                {searching ? 'Searching…' : 'Search results'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {searchResults.length
                                    ? searchResults.map(entity => (
                                          <PartnerListRow
                                              key={entity._id}
                                              entity={entity}
                                              selected={isSelected(entity)}
                                              onToggle={() => toggleEntity(entity)}
                                          />
                                      ))
                                    : !searching && (
                                          <Typography
                                              sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic', py: 2 }}>
                                              No matches for &ldquo;{searchTerm.trim()}&rdquo;
                                          </Typography>
                                      )}
                            </Box>
                        </Box>
                    ) : null}

                    {filteredRecent.length > 0 ? (
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                    fontSize: 10,
                                    fontWeight: 900,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: midnight.onSurfaceVariant,
                                    mb: 2,
                                }}>
                                Recent interactions
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, minmax(0, 1fr))',
                                        sm: 'repeat(3, minmax(0, 1fr))',
                                    },
                                    gap: 2,
                                }}>
                                {filteredRecent.map(entry => {
                                    const entity = recentEntryToHost(entry) as IPartner;
                                    return (
                                        <RecentGridCard
                                            key={entry._id}
                                            entry={entry}
                                            active={isSelected(entity)}
                                            onSelect={() => toggleEntity(entity)}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : !showSearchResults ? (
                        <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic', py: 2 }}>
                            Search for a {searchType}, or create a new one with the + button below.
                        </Typography>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        px: 3,
                        pt: 2,
                        pb: isMobile ? 4 : 3,
                        background: `linear-gradient(to top, ${midnight.surface} 65%, transparent)`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}>
                    <IconButton
                        onClick={onCreateNew}
                        aria-label={`Create new ${searchType}`}
                        sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            bgcolor: midnight.onSurface,
                            color: midnight.surface,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            '&:hover': { bgcolor: '#f0f0f0' },
                        }}>
                        <AddIcon />
                    </IconButton>
                    <Box
                        component="button"
                        type="button"
                        onClick={handleConfirm}
                        sx={{
                            flex: 1,
                            height: 64,
                            border: 'none',
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            background: `linear-gradient(to right, ${midnight.primary}, #feb700)`,
                            boxShadow: '0 8px 32px rgba(254, 183, 0, 0.25)',
                            transition: 'transform 0.15s',
                            '&:active': { transform: 'scale(0.97)' },
                        }}>
                        <Typography
                            sx={{
                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                fontWeight: 900,
                                fontSize: 12,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: midnight.primaryFixed,
                            }}>
                            Confirm selection
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 20, color: midnight.primaryFixed, fontWeight: 700 }} />
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
